import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import { getMyMigrationCode } from '../services/migrationService';

/**
 * Estado compartido del código de migración a ActionEV.
 *
 * Se consulta una vez por inicio de sesión (cuando aparece el token) y lo
 * comparten el modal que sale al entrar y las tarjetas de Billetera/Ajustes.
 * Si el backend responde 404 (sin código, o admin), `code` queda en null y
 * ninguno de los dos muestra nada.
 */
const MigrationContext = createContext({
  code: null,
  loading: false,
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  refresh: () => {},
});

export const useMigration = () => useContext(MigrationContext);

const MODAL_SHOWN_KEY = 'migrationCodeModalShown';

export const MigrationProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) {
      setCode(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getMyMigrationCode();
      setCode(data);
      if (data) {
        let yaMostrado = false;
        try {
          yaMostrado = sessionStorage.getItem(MODAL_SHOWN_KEY) === '1';
        } catch (e) {
          yaMostrado = false;
        }
        if (!yaMostrado) {
          setIsModalOpen(true);
        }
      }
    } catch (error) {
      // Un fallo aquí no debe estorbar el resto de la app.
      console.error('No se pudo consultar el código de migración', error);
      setCode(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    try {
      sessionStorage.setItem(MODAL_SHOWN_KEY, '1');
    } catch (e) {
      // sessionStorage bloqueado: el modal volverá a salir en la próxima carga, nada grave.
    }
  }, []);

  return (
    <MigrationContext.Provider value={{ code, loading, isModalOpen, openModal, closeModal, refresh }}>
      {children}
    </MigrationContext.Provider>
  );
};

export default MigrationProvider;
